import Enumerable from "linq";
import { LoggerFactory } from "../../../utilities/logger";
import { IFreezable } from "..";
import { IEditCommandManageable } from "./IEditCommandManageable";
import { IEditCommand, EditCommand, EditCommandSet, EditCommandManager, IEditCommandFactory, WriteValueCommand } from "./command";
import { ISourceReferable } from "./ISourceReferable";
import { SourceReader } from "./SourceReader";
import { IPropertyPresentable } from "./IPropertyPresentable";
import { IPropertyEditable } from "./IPropertyEditable";
import { PropertyPresenter } from "./PropertyPresenter";
import { PropertyEditor } from "./PropertyEditor";
import { IPropertyPresenterCreatable } from "./IPropertyPresenterCreatable";
import { IPropertyEditorCreatable } from "./IPropertyEditorCreatable";

export abstract class AbstractSourceEditor<TSource> implements ISourceReferable<TSource>, IPropertyPresenterCreatable<TSource>, IPropertyEditorCreatable<TSource>, IEditCommandManageable, IFreezable {
  constructor(source: TSource | AbstractSourceEditor<TSource>) {
    if (source instanceof AbstractSourceEditor) {
      this._source = source._source;
      this._commandManager = source._commandManager;
    } else {
      this._source = new SourceReader(source);
      this._commandManager = new EditCommandManager();
    }
  }

  private readonly _source: SourceReader<TSource>;
  private _commandManager: EditCommandManager<TSource>;

  setCommandCountMax(value: number): void {
    this._commandManager = new EditCommandManager(value);
  }

  public getSource(): TSource {
    return this._source.getSource();
  }

  public isReferedSameSource(instance: ISourceReferable<TSource>): boolean {
    return (this.getSource() === instance.getSource());
  }

  protected getOrDefault<T>(get: (s: TSource) => T, defaultValue: T): T {
    return this._source.getOrDefault(get, defaultValue);
  }

  protected do(command: IEditCommand<TSource>): void {
    LoggerFactory.getLogger().info("★do command.");
    // console.log(command); // 調査用のログ.何の更新か分からない時はコメント外してログ出してchrome等で見てcommandの奥深くで設定されてる値とかを覗けばある程度アタリがつく.
    this._commandManager.do(this._source._source, command);
    this.onDone();
  }

  protected onDone(): void {
    // doによるリカバリが必要なら派生クラスでoverrideすること.
  }

  protected apply(factories: IEditCommandFactory<TSource> | IEditCommandFactory<TSource>[]): void {
    if (!factories) {
      return;
    } else if (!Array.isArray(factories)) {
      this.apply([ factories ]);
    } else {
      const commands = Enumerable.from(factories)
        .where(f => !!f)
        .select(f => f.createCommand())
        .where(c => !!c)
        .toArray();
      if (0 < commands.length) {
        this.do(new EditCommandSet(commands));
      }
    }
  }

  public createPropertyPresenter<T>(get: (s: TSource) => T): IPropertyPresentable<T> {
    return new PropertyPresenter({
      source: this._source._source,
      read: get,
    });
  }

  public createPropertyEditor<T>(get: (s: TSource) => T, set: (s: TSource, v: T) => void, isSettable?: (s: TSource) => boolean): IPropertyEditable<T> {
    return new PropertyEditor({
      source: this._source._source,
      read: get,
      write: v => this.do(new WriteValueCommand(get, set, v)),
      isWritable: isSettable,
    });
  }

  public createPropertiesEditor<TValue, TValues>(get: (s: TSource) => TValues, toValue: (vs: TValues) => TValue, set: (s: TSource, v: TValue) => void, revert: (s: TSource, vs: TValues) => void, isSettable?: (s: TSource) => boolean): IPropertyEditable<TValue> {
    return new PropertyEditor({
      source: this._source._source,
      read: s => toValue(get(s)),
      write: v => this.do(new EditCommand(get, s => set(s, v), revert)),
      isWritable: isSettable,
    });
  }

  hasDoneCommands(): boolean {
    return this._commandManager.hasDoneCommands;
  }

  hasUndoneCommands(): boolean {
    return this._commandManager.hasUndoneCommands;
  }

  undo(): void {
    LoggerFactory.getLogger().info("★undo last command.");
    this._commandManager.undo(this._source._source);
    this.onUndone();
  }

  protected onUndone(): void {
    // undoによるリカバリが必要なら派生クラスでoverrideすること.
  }

  redo(): void {
    LoggerFactory.getLogger().info("★redo last undone command.");
    this._commandManager.redo(this._source._source);
    this.onDone();
  }

  getResult(): TSource {
    return this._source._source;
  }

  freeze(): void {
    Object.freeze(this);
  }
}
